<template>
    <div class="query-section">
        <h3 v-if="title">{{ title }}:</h3>
        <div v-if="textResponse" class="explanation-text">
            <div class="markdown-content custom-class">
                <Markdown :content="textResponse" />
            </div>
            <!-- {{ textResponse }} -->
        </div>

        <pre v-if="query" class="bg-light p-3 rounded">{{ query }}</pre>

        <div :class="['mt-3', isChatHistory ? '' : 'mb-4']" v-if="!isChatHistory || query">
            <button v-if="!hideButtons" class="btn btn-success me-2" @click="executeQuery" :disabled="localLoading || queryExecuted">
                Run Query
            </button>
            <button class="btn btn-secondary" @click="handleSaveQuery" :disabled="localLoading">
                Save Query
            </button>
        </div>

        <div v-if="isChatHistory">
            <div v-if="queryResultsProp">
                <h3>Query Results:</h3>
                <div v-if="queryResultsProp.error" class="alert alert-danger">
                    {{ queryResultsProp.error }}
                </div>
                <div v-else class="table-responsive">
                    <table class="table table-striped table-bordered">
                        <thead>
                            <tr>
                                <th v-for="(column, index) in queryResultsProp.column_info" :key="index">
                                    {{ column.name }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, rowIndex) in queryResultsProp.data" :key="rowIndex">
                                <td v-for="(column, colIndex) in queryResultsProp.column_info" :key="colIndex">
                                    {{ row[colIndex] }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div v-if="analysisResultProp" class="mt-4">
                <h3>Analysis:</h3>
                <div class="explanation-text">
                    <div class="markdown-content">
                        <Markdown :content="analysisResultProp" />
                    </div>
                    <!-- {{ analysisResultProp }} -->
                </div>
                <div v-if="adjustedQueryProp">
                    <QueryDisplay title="Adjusted Query" :query="adjustedQueryProp" :loading="loading" :isChatHistory="true"
                        :question="question" :database="database" :table="table" :resourceId="resourceId" :messagesList="messagesList"
                        :hideButtons="hideButtons" @loading-change="$emit('loading-change', $event)"
                        @save-query="$emit('save-query', $event)" @append-message="$emit('append-message', $event)" />
                </div>
            </div>
        </div>
        <div v-else>
            <div v-if="agentStatusList?.length > 0 || localLoading" class="mb-4">
                <div class="agent-status collapsed" role="button" data-bs-toggle="collapse" :data-bs-target="'#agentStatusCollapse'">
                    <i v-if="localLoading" class="fa-solid fa-circle fa-beat-fade text-success fs-5"></i>
                    <i v-else :class="['fa-solid', guardrailBlocked == true ? 'fa-xmark text-danger slight-margin-down' : 'fa-check text-success']"></i>
                    <span class="status-text text-muted fst-italic fw-bold"> {{ getCurrentAgentStatus }} </span>
                    <!-- Conditionally display any one icon based on collapsed -->
                    <i class="fa-solid fa-angle-down text-muted collapse-arrow-down" style="font-size: 0.75rem; margin-top: 0.15rem;"></i>
                    <i class="fa-solid fa-angle-up text-muted collapse-arrow-up" style="font-size: 0.75rem; margin-top: 0.15rem;"></i>
                </div>
                <div v-if="agentStatusList.length > 0" class="status-text text-muted mt-2 collapse" :id="'agentStatusCollapse'">
                    <div class="list-unstyled">
                        <div v-for="(status, index) in agentStatusList" :key="index" class="row py-2 mx-0 border-bottom">
                            <div class="col-2 px-0 pe-2 d-flex align-items-top" style="word-break: normal;">
                                <span class="d-inline-flex justify-content-center me-2" style="width: 1.3rem;">
                                    <div>
                                        <i :class="getAgentStatusIcon(status.label, streaming = (localLoading && currentStatus(agentStatusList)?.[index] == true))"></i>
                                    </div>
                                </span>
                                <span class="fw-bold">{{ status.label }}</span>
                            </div>
                            <div class="col-8 px-0">
                                <span>{{ status.detail }}</span>
                            </div>
                            <div class="col-2 text-end px-0">
                                <span>{{ status.timestamp }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="queryResults">
                <h3>Query Results:</h3>
                <div v-if="queryResults.error" class="alert alert-danger">
                    {{ queryResults.error }}
                </div>
                <div v-else class="table-responsive">
                    <table class="table table-striped table-bordered">
                        <thead>
                            <tr>
                                <th v-for="(column, index) in queryResults.column_info" :key="index">
                                    {{ column.name }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(row, rowIndex) in queryResults.data" :key="rowIndex">
                                <td v-for="(column, colIndex) in queryResults.column_info" :key="colIndex">
                                    {{ row[colIndex] }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div v-if="analysisResult" class="mt-4">
                <h3>Analysis:</h3>
                <div class="explanation-text">
                    <div class="markdown-content">
                        <Markdown :content="analysisResult" />
                    </div>
                    <!-- {{ analysisResult }} -->
                </div>

                <div v-if="adjustedQuery">
                    <QueryDisplay title="Adjusted Query" :query="adjustedQuery" :loading="loading" :question="question"
                        :database="database" :table="table" :resourceId="resourceId" :messagesList="messagesList" :hideButtons="hideButtons"
                        @loading-change="$emit('loading-change', $event)" @save-query="$emit('save-query', $event)" 
                        @append-message="$emit('append-message', $event)"/>
                </div>
            </div>
            <div v-if="guardrailBlocked == true">
                <div class="blocked-message-text">
                    <span>{{ guardrailMessage }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Markdown from "../components/Markdown.vue";
import untruncateJson from "untruncate-json";
import { mapActions } from 'vuex';
import { mergeAgentsQueryIntoUrl } from '@/utils/agentsApi';
import { getAgentStatusIcon, currentStatus } from '@/utils/commonFunction'
export default {
    components: { Markdown },
    name: 'QueryDisplay',
    props: {
        title: {
            type: String,
            required: true
        },
        query: {
            type: String,
            required: true
        },
        textResponse: {
            type: String,
            default: ''
        },
        loading: {
            type: Boolean,
            default: false
        },
        question: {
            type: String,
            required: true
        },
        database: {
            type: String,
            required: true
        },
        table: {
            type: String,
            required: true
        },
        analysisResultProp: {
            type: String,
            default: ""
        },
        adjustedQueryProp: {
            type: String,
            default: ""
        },
        queryResultsProp: {
            type: Object,
            default: null
        },
        resourceId: {
            type: String,
            default: ""
        },
        messagesList: {
            type: Array,
            default: () => []
        },
        isChatHistory: {
            type: Boolean,
            default: false
        },
        hideButtons: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            guardrailBlocked: false,
            guardrailMessage: "",
            queryExecuted: false,
            queryResults: null,
            analysisResult: null,
            localLoading: false,
            agentStatus: null,
            agentStatusList: [],
        }
    },
    computed: {
        getCurrentAgentStatus() {
            if (this.guardrailBlocked) return "Blocked";
            if (this.agentStatus) return `${this.agentStatus}...`;
            else if (this.localLoading) return "Loading...";
            else return this.agentStatusList?.length > 0 ? `Done - ${this.agentStatusList.length} steps` : "Done";
        }
    },
    methods: {
        ...mapActions({
            upsertChatMessage: "amdp/upsertChatMessage",
        }),
        untruncateJson,
        getAgentStatusIcon,
        currentStatus,
        async executeQuery() {
            this.$emit('loading-change', true);
            this.agentStatus = null;
            this.localLoading = true;
            this.queryExecuted = true;
            this.quardrailBlocked = false;
            this.guardrailMessage = "";
            console.log("Query: ", this.query)

            // create user message to chat (for execute query api)
            let user_message = {
                "role": "user",
                "content": "Execute the query and give results. In case of error, adjust the query.",
                "timestamp": new Date().toISOString(),
            }
            await this.upsertChatMessage({
                feature: "talk_to_my_data",
                resourceId: this.resourceId,
                message_payload: {
                    "messages": [user_message]
                },
            }).then(() => {
                this.$emit('append-message', user_message);
            }).catch((error) => {
                let err = error.response?.data.error ? error.response.data.error : error.message;
                console.error('Failed to create user message:', err);
                this.$store.dispatch('addToast', {
                    type: 'error',
                    message: `Error creating user message: ${err}`,
                });
            });

            try {
                const endpoint = mergeAgentsQueryIntoUrl(
                    `${this.$store.state.agentsUrl}/v1/talk_to_my_data/execute_query`,
                    this.$store.state
                );
                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${this.$store.state.accessToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        question: this.question,
                        generated_query: this.query,
                        db_name: this.database,
                        table_name: this.table,
                        access_token: this.$store.state.accessToken,
                        cid: this.$store.state.currentClient.cid,
                        messages: this.messagesList,
                    })
                });

                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                let forever = true
                while (forever) {
                    const { value, done } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n\n');

                    // Process all complete lines, provided no guardrail block has been detected
                    for (let i = 0; i < lines.length - 1 && !this.guardrailBlocked; i++) {
                        const line = lines[i].trim();

                        const eventMatch = line.match(/^event: (.+)$/m);
                        const dataMatch = line.match(/^data: (.+)$/m);

                        if (eventMatch && dataMatch) {
                            const eventType = eventMatch[1];
                            const dataStr = dataMatch[1];

                            console.log('Event type:', eventType, 'Data:', dataStr);

                            if (eventType == 'message') {
                                try {
                                    const data = JSON.parse(this.untruncateJson(dataStr));
                                    if (typeof data === 'object' && data.final_answer) {
                                        this.queryResults = data.query_result;
                                        this.analysisResult = data.final_answer;
                                        this.adjustedQuery = data.adjusted_query;
                                    }
                                } catch (e) {
                                    console.error('Error in message chunk:', e);
                                }
                            } else if (eventType == 'blocked') {
                                try {
                                    const defaultReason = 'Sorry your input has been blocked by our guardrails';
                                    const data = JSON.parse(dataStr);
                                    this.guardrailMessage = data.reason || defaultReason;
                                    let toastoptions = {
                                        message: defaultReason,
                                        type: "error"
                                    }
                                    this.$store.dispatch('addToast', toastoptions);

                                    // add guardrail message to chat
                                    let guardrail_message = {
                                        "role": "assistant",
                                        "content": {
                                            "guardrail_action": "guardrail_intervened",
                                            "guardrail_message": this.guardrailMessage,
                                        },
                                        "timestamp": new Date().toISOString(),
                                    }
                                    await this.upsertChatMessage({
                                        feature: "talk_to_my_data",
                                        resourceId: this.resourceId,
                                        message_payload: {
                                            "messages": [guardrail_message]
                                        },
                                    });
                                } catch (e) {
                                    console.error('Error in blocked chunk:', e);
                                } finally {
                                    this.guardrailBlocked = true;
                                    forever = false; // stop reading further
                                }
                            } else if (eventType == 'status') {
                                try {
                                    let parsedData = JSON.parse(dataStr);
                                    let label = parsedData.label;
                                    let detail = parsedData.detail || '';
                                    let timestamp = parsedData.timestamp || new Date().toISOString().replace(/T/, ' ').replace(/Z/, '');

                                    this.agentStatus = label;
                                    this.agentStatusList.push({ label, detail, timestamp });
                                } catch (e) {
                                    console.error('Error in status chunk:', e);
                                }
                            }
                        }
                    }

                    // Keep the last incomplete line in the buffer
                    buffer = lines[lines.length - 1];
                }

                if (!this.guardrailBlocked) {
                    // create agent message to chat
                    let agent_message = {
                        "role": "assistant",
                        "content": {
                            "query_result": this.queryResults,
                            "adjusted_query": this.adjustedQuery,
                            "final_answer": this.analysisResult,
                        },
                        "timestamp": new Date().toISOString(),
                    }
                    await this.upsertChatMessage({
                        feature: "talk_to_my_data",
                        resourceId: this.resourceId,
                        message_payload: {
                            "messages": [agent_message]
                        },
                    }).then(() => {
                        this.$emit('append-message', agent_message);
                    }).catch((error) => {
                        let err = error.response?.data.error ? error.response.data.error : error.message;
                        console.error('Failed to create agent message:', err);
                        this.$store.dispatch('addToast', {
                            type: 'error',
                            message: `Error creating agent message: ${err}`,
                        });
                    });
                }

            } catch (error) {
                console.error('Error executing query:', error);
                this.queryExecuted = false;
            } finally {
                this.localLoading = false;
                this.$emit('loading-change', false);
                this.agentStatus = null;
            }
        },
        handleSaveQuery() {
            this.$emit('save-query', this.query);
        }
    },
    emits: ['loading-change', 'save-query', 'append-message'],
}
</script>

<style scoped>

.table-responsive {
    max-height: 400px;
    overflow-y: auto;
}

table {
    margin-bottom: 0;
    background-color: white;
}

thead th {
    position: sticky;
    top: 0;
    background-color: #fff;
    z-index: 1;
}

.custom-class {
    width: 100%;
    overflow-x: scroll;
}

.agent-status {
    display: flex;
    align-items: center;
    gap: 8px;
}

.status-text {
    font-size: 13px;
}

.border-bottom {
  padding-top: 0.375rem;
  padding-bottom: 0.375rem;
}
.border-bottom:last-child {
  border-bottom: 0 !important;
}

/* When collapsed, hide the UP arrow */
.agent-status.collapsed .collapse-arrow-up {
    display: none !important;
}

/* When expanded (not collapsed), hide the DOWN arrow */
.agent-status:not(.collapsed) .collapse-arrow-down {
    display: none !important;
}

#agentStatusCollapse {
    max-height: 300px;
    overflow-y: scroll;
}
.blocked-message-text {
  background: #fdf2f2;
  color: #5c1b1b;
  border: 1.2px solid #dc3545;
  font-style: italic;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
}
.slight-margin-down {
    margin-bottom: 0.7px;
}
</style>

<style>
body.dark-mode .generated-query-display table {
    background-color: #1a1d23 !important;
    color: #e1e3e8;
}
body.dark-mode .generated-query-display thead th {
    background-color: #111317 !important;
    color: #a0a4b8;
}
body.dark-mode .generated-query-display td {
    color: #e1e3e8;
    border-color: #2e3238;
}
</style>